import { useEffect, useState } from "react";
import { Trophy, Crown, Medal, Gift } from "lucide-react";
import api from "@/lib/api";
import { fmt, fmtDate, EmptyState } from "@/components/shared";

const rankIcon = (r) => r === 1 ? <Crown className="w-4 h-4 text-amber-300" /> : r <= 3 ? <Medal className="w-4 h-4 text-slate-300" /> : <span className="font-mono text-xs text-slate-500">#{r}</span>;
const badgeCls = { 1: "bg-amber-500/15 text-amber-300 border-amber-500/40", 2: "bg-slate-300/15 text-slate-200 border-slate-300/40", 3: "bg-orange-700/20 text-orange-300 border-orange-600/40" };

export function TrophyBadge({ rank, label, testId }) {
  return (
    <span data-testid={testId} title={label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badgeCls[rank] || badgeCls[3]}`}>
      <Trophy className="w-3 h-3" /> #{rank}{label ? ` · ${label}` : ""}
    </span>
  );
}

export function ReferralLeaderboard() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/referrals/leaderboard", { params: { period } }).then((r) => setData(r.data)).catch(() => setData(false)); }, [period]);

  return (
    <div className="glass rounded-2xl p-6" data-testid="referral-leaderboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-300" />
          <div><h3 className="font-heading text-lg text-white">Top Referrers</h3><p className="text-xs text-slate-500" data-testid="leaderboard-period">{data?.period_label || "…"}</p></div>
        </div>
        <div className="flex gap-2">
          {[["month", "This Month"], ["all", "All Time"]].map(([v, l]) => (
            <button key={v} data-testid={`leaderboard-${v}`} onClick={() => setPeriod(v)}
              className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${period === v ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>{l}</button>
          ))}
        </div>
      </div>

      {data?.bonus_enabled && (
        <div className="flex flex-wrap items-center gap-2 mb-5 text-xs" data-testid="leaderboard-prizes">
          <span className="text-slate-500 inline-flex items-center gap-1"><Gift className="w-3.5 h-3.5 text-fuchsia-300" /> Monthly bonus:</span>
          {data.prizes.map((p, i) => (
            <span key={i} className={`px-2.5 py-1 rounded-full border font-mono ${badgeCls[i + 1]}`}>#{i + 1} · {fmt(p)} USDT</span>
          ))}
          <span className="text-slate-600">· closes {fmtDate(data.closes_at)}</span>
        </div>
      )}

      {data === null ? <p className="text-sm text-slate-500 py-6 text-center">Loading…</p> : !data || data.entries.length === 0 ? (
        <EmptyState icon={Trophy} title="No referrers yet" subtitle="Be the first on the board — share your link today." />
      ) : (
        <div className="space-y-2">
          {data.entries.map((e) => (
            <div key={e.rank} data-testid={`leaderboard-row-${e.rank}`}
              className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${e.is_me ? "bg-cyan-500/10 border-cyan-500/40 shadow-[0_0_18px_rgba(0,240,255,0.15)]" : "bg-slate-900/40 border-white/5"}`}>
              <div className="w-7 flex justify-center shrink-0">{rankIcon(e.rank)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200 truncate flex items-center gap-2 flex-wrap">{e.name}
                  {e.is_me && <span className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold" data-testid="leaderboard-you">You</span>}
                  {e.badge && <TrophyBadge rank={e.badge} testId={`leaderboard-badge-${e.rank}`} />}
                </p>
                <p className="text-xs text-slate-500">{e.signups} signup{e.signups === 1 ? "" : "s"} · {e.rewarded} rewarded</p>
              </div>
              <p className="font-mono text-sm text-emerald-300 shrink-0">+{fmt(e.rewards)} USDT</p>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div className="mt-5 pt-4 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm" data-testid="leaderboard-me">
          <span className="text-slate-400">Your standing</span>
          <span className="font-mono text-slate-200">
            {data.me.rank ? `#${data.me.rank} of ${data.me.total_ranked}` : "Unranked"} · {data.me.signups} signups · +{fmt(data.me.rewards)} USDT
          </span>
        </div>
      )}
      {data?.my_awards?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="leaderboard-my-awards">
          {data.my_awards.map((a) => <TrophyBadge key={a.month} rank={a.rank} label={a.month_label} testId={`my-award-${a.month}`} />)}
        </div>
      )}
    </div>
  );
}
